import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, X, Plus, ArrowRight, Swap, WarningCircle } from "@phosphor-icons/react";
import BOQItemPicker from "@/components/BOQItemPicker";

const fmtQty = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);

export default function TransferCreate() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { activeProject, activeProjectId, projects, loading: projectsLoading } = useProject();

  const [categories, setCategories] = useState([]);
  const [sourceStock, setSourceStock] = useState([]); // rows from /stock for source project
  const [loadingStock, setLoadingStock] = useState(false);

  const [form, setForm] = useState({
    to_project_id: "",
    category: "",
    unit: "nos",
    quantity: "",
    notes: "",
    boq_item_id: null,
  });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load category list + source-project live stock
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stock/categories");
        setCategories(data.categories || []);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!activeProjectId) return;
    setLoadingStock(true);
    (async () => {
      try {
        // explicitly fetch with project_id so interceptor doesn't double-up
        const { data } = await api.get("/stock", { params: { page: 1, page_size: 500, project_id: activeProjectId } });
        setSourceStock(data.items || []);
      } catch (_) {
        setSourceStock([]);
      } finally {
        setLoadingStock(false);
      }
    })();
  }, [activeProjectId]);

  // When category changes, default unit to the most common unit present in source stock for that category
  useEffect(() => {
    if (!form.category) return;
    const match = sourceStock.find((r) => r.category === form.category && r.available > 0);
    if (match) {
      setForm((f) => ({ ...f, unit: match.unit }));
    }
  }, [form.category, sourceStock]);

  const otherProjects = useMemo(
    () => projects.filter((p) => p.id !== activeProjectId),
    [projects, activeProjectId]
  );

  // Available source stock for the chosen category+unit
  const availableAtSource = useMemo(() => {
    if (!form.category) return null;
    const row = sourceStock.find(
      (r) => r.category === form.category && (r.unit || "").toLowerCase() === (form.unit || "").toLowerCase()
    );
    return row ? row.available : 0;
  }, [form.category, form.unit, sourceStock]);

  const qtyNum = parseFloat(form.quantity) || 0;
  const exceedsAvailable = availableAtSource !== null && qtyNum > availableAtSource;

  const canSave =
    form.to_project_id &&
    form.category &&
    form.boq_item_id &&
    form.unit &&
    qtyNum > 0 &&
    !exceedsAvailable &&
    !saving;

  const onPhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type.startsWith("image/"));
    const newPhotos = valid.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }));
    setPhotos((prev) => [...prev, ...newPhotos]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async () => {
    if (!canSave) {
      if (!form.boq_item_id) { toast.error("Please pick the BOQ item this transfer is against."); return; }
      toast.error("Fill destination, category, qty and ensure qty ≤ available stock.");
      return;
    }
    if (!activeProjectId) {
      toast.error("No active project selected.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("from_project_id", activeProjectId);
      fd.append("to_project_id", form.to_project_id);
      fd.append("category", form.category);
      fd.append("unit", form.unit || "nos");
      fd.append("quantity", String(qtyNum));
      fd.append("notes", form.notes || "");
      fd.append("boq_item_id", form.boq_item_id);
      photos.forEach((p) => fd.append("photos", p.file, p.file.name));

      await api.post("/transfers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Transfer submitted — awaiting admin approval");
      navigate("/transfers");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toProject = projects.find((p) => p.id === form.to_project_id);

  return (
    <div className="p-8 sm:p-10 max-w-[1200px] mx-auto" data-testid="transfer-create-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">Logistics</div>
        <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
          <Swap size={32} className="text-primary" weight="fill" />
          New Site Transfer
        </h1>
        <p className="text-sm text-neutral-500 mt-2">
          Move material from <strong>{activeProject?.name || "the active project"}</strong> to another site.
          Stock will rebalance on both projects <strong>only after an admin approves the delivery</strong>.
        </p>
      </div>

      {projectsLoading ? (
        <div className="bg-white border border-neutral-200 rounded-sm p-10 text-center text-sm text-neutral-500" data-testid="transfer-loading">
          Loading projects…
        </div>
      ) : projects.length < 2 ? (
        <div className="bg-[#FEF7E0] border border-[#FCE39E] text-[#B06000] rounded-sm p-4 text-sm" data-testid="transfer-no-other-projects">
          <WarningCircle size={18} className="inline mr-2" weight="fill" />
          You need at least two configured projects to transfer material. Ask an admin to create another project.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {/* Routing */}
            <div className="bg-white border border-neutral-200 rounded-sm p-6">
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Routing</div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-end gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">From (Source)</Label>
                  <div
                    className="rounded-sm h-11 px-3 flex items-center gap-2 border border-neutral-200 bg-neutral-50"
                    data-testid="transfer-from-project"
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: activeProject?.color || "#0047FF" }}
                    />
                    <span className="text-sm">{activeProject?.name || "—"}</span>
                  </div>
                </div>
                <div className="hidden md:flex items-center justify-center pb-2.5">
                  <ArrowRight size={24} className="text-neutral-400" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">To (Destination) *</Label>
                  <Select
                    value={form.to_project_id}
                    onValueChange={(v) => setForm({ ...form, to_project_id: v })}
                  >
                    <SelectTrigger className="rounded-sm h-11" data-testid="transfer-to-select">
                      <SelectValue placeholder="Select destination project" />
                    </SelectTrigger>
                    <SelectContent>
                      {otherProjects.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`to-option-${p.code}`}>
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ backgroundColor: p.color || "#0047FF" }}
                            />
                            <span>{p.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Material */}
            <div className="bg-white border border-neutral-200 rounded-sm p-6">
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Material</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <BOQItemPicker
                    projectId={activeProjectId}
                    value={form.boq_item_id}
                    onChange={(id) => setForm({ ...form, boq_item_id: id })}
                    required
                    label="BOQ Item (source project)"
                    testId="transfer-boq-picker"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Category *</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger className="rounded-sm h-11" data-testid="transfer-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Unit *</Label>
                  <Input
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    placeholder="nos / kg / bags / cum…"
                    className="rounded-sm h-11"
                    data-testid="transfer-unit-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Quantity *</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className={`rounded-sm h-11 text-right tabular-nums ${exceedsAvailable ? "border-destructive" : ""}`}
                    data-testid="transfer-qty-input"
                  />
                  {form.category && (
                    <div
                      className={`text-[11px] mt-1 ${exceedsAvailable ? "text-destructive" : "text-neutral-500"}`}
                      data-testid="transfer-available-hint"
                    >
                      {loadingStock
                        ? "Checking source stock…"
                        : exceedsAvailable
                        ? `Only ${fmtQty(availableAtSource)} ${form.unit} available at source`
                        : `Available at ${activeProject?.name}: ${fmtQty(availableAtSource ?? 0)} ${form.unit}`}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-600">Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="rounded-sm min-h-[80px]"
                    placeholder="Vehicle no., reason for transfer, batch reference…"
                    data-testid="transfer-notes-input"
                  />
                </div>
              </div>
            </div>

            {/* Photos */}
            <div className="bg-white border border-neutral-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Photo Trail</div>
                  <div className="text-sm text-neutral-700 mt-0.5">Loading slip, vehicle, gate-pass — multi-photo proof</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-sm h-9 gap-1.5"
                  data-testid="transfer-add-photos-btn"
                >
                  <Camera size={14} /> Add Photos
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  onChange={onPhotoSelect}
                  className="hidden"
                  data-testid="transfer-photo-input"
                />
              </div>
              {photos.length === 0 ? (
                <div className="border border-dashed border-neutral-300 rounded-sm py-10 text-center">
                  <Camera size={28} className="mx-auto text-neutral-400 mb-2" />
                  <div className="text-sm text-neutral-500">No photos added</div>
                  <div className="text-xs text-neutral-400 mt-1">Tap "Add Photos" — proof recommended for inter-site transfers</div>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {photos.map((p, i) => (
                    <div key={i} className="relative group" data-testid={`transfer-photo-preview-${i}`}>
                      <img
                        src={p.previewUrl}
                        alt={`Photo ${i + 1}`}
                        className="w-full aspect-square object-cover rounded-sm border border-neutral-200"
                      />
                      <button
                        onClick={() => removePhoto(i)}
                        className="absolute top-1 right-1 h-6 w-6 bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`transfer-remove-photo-${i}`}
                      >
                        <X size={12} />
                      </button>
                      <div className="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/50 px-1 truncate">
                        {p.file.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sticky summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-neutral-200 rounded-sm p-6 lg:sticky lg:top-8" data-testid="transfer-summary">
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Summary</div>
              <div className="space-y-2.5 text-sm mb-6">
                <Row label="From" value={activeProject?.name || "—"} />
                <Row label="To" value={toProject?.name || "—"} />
                <Row label="Category" value={form.category || "—"} />
                <Row label="Quantity" value={form.quantity ? `${form.quantity} ${form.unit}` : "—"} />
                <Row label="Photos" value={photos.length > 0 ? `${photos.length} attached` : "None"} />
                {form.category && (
                  <Row
                    label="Source Stock"
                    value={`${fmtQty(availableAtSource ?? 0)} ${form.unit}`}
                    accent={exceedsAvailable ? "text-destructive" : "text-neutral-700"}
                  />
                )}
              </div>
              {exceedsAvailable && (
                <div className="mb-4 text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-sm p-2.5 flex items-start gap-2" data-testid="transfer-error-banner">
                  <WarningCircle size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
                  <span>Transfer exceeds available source stock. Adjust quantity to continue.</span>
                </div>
              )}
              <Button
                className="w-full rounded-sm h-11 bg-primary hover:bg-[#0039CC] text-white font-medium gap-2"
                onClick={handleSubmit}
                disabled={!canSave}
                data-testid="save-transfer-btn"
              >
                <Plus size={16} weight="bold" />
                {saving ? "Submitting…" : "Submit for Admin Approval"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-neutral-500 text-xs uppercase tracking-wider">{label}</span>
      <span className={`text-right ${accent || ""}`}>{value}</span>
    </div>
  );
}
